/**
 * Shared form field rendering components extracted from FormRendererPage.
 * Used by the encounter form renderer, registration form page, and grouped edit pages.
 */
import { useState, useMemo, useRef, useEffect, type CSSProperties } from 'react';
import { NavIcon } from '../../../shell/shared';
import {
  type FormFieldDef,
  type FormFieldsSchemaV1,
  type FormSection,
  type FormTemplatePageRow,
  type FormTemplateMappingRow,
  resolveBindingKey,
  isFieldEditable,
  parseRichText,
  stringifyRichText,
  resolveEditableRichText,
  type RichTextConfig,
} from '@helios/data-access';
import { RichTextEditor, RichTextView } from '../../../components/RichTextField';

// ─── Value Resolution ───

/**
 * Resolve the runtime value for a field, respecting binding mode precedence.
 */
export function resolveBoundFieldValue(
  field: FormFieldDef,
  storedValues: Record<string, string>,
  bindingData: Record<string, string>,
): string | undefined {
  if (!field.binding) {
    return storedValues[field.key] ?? undefined;
  }
  const boundKey = resolveBindingKey(field);
  const boundValue = boundKey ? bindingData[boundKey] : undefined;

  if (field.binding.mode === 'read_only') {
    return boundValue ?? undefined;
  }
  const stored = storedValues[field.key];
  if (stored !== undefined && stored !== '') return stored;
  return boundValue ?? undefined;
}

/**
 * Resolve display value for a field instance. Used by template overlay and
 * values-summary fallback views. Returns a display string (never undefined).
 */
export function resolveInstanceValue(
  field: FormFieldDef | undefined,
  values: Record<string, string>,
  signatures: Record<string, string>,
  bindingData: Record<string, string>,
): string {
  if (!field) return '—';
  if (field.type === 'drawing') return field.label || 'Drawing';
  if (field.type === 'signature') return signatures[field.key] ? '(signed)' : '(unsigned)';
  return resolveBoundFieldValue(field, values, bindingData) || '—';
}

// ─── Signature Pad ───

export function SignaturePad({
  fieldKey,
  existingData,
  onSave,
  readOnly,
}: {
  fieldKey: string;
  existingData: string | null;
  onSave: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [drawing, setDrawing] = useState(false);
  const [hasContent, setHasContent] = useState(false);
  const strokesRef = useRef<Array<{ x: number; y: number }[]>>([]);
  const currentStrokeRef = useRef<{ x: number; y: number }[]>([]);

  useEffect(() => {
    if (existingData && canvasRef.current) {
      try {
        const parsed = JSON.parse(existingData);
        if (parsed.strokes && canvasRef.current) {
          strokesRef.current = parsed.strokes;
          redraw();
          setHasContent(true);
        }
      } catch {
        // ignore
      }
    }
  }, [existingData]);

  const getPos = (e: React.PointerEvent): { x: number; y: number } => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();
    return {
      x: (e.clientX - rect.left) * (canvas.width / rect.width),
      y: (e.clientY - rect.top) * (canvas.height / rect.height),
    };
  };

  const redraw = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const stroke of strokesRef.current) {
      if (stroke.length < 2) continue;
      ctx.beginPath();
      ctx.moveTo(stroke[0].x, stroke[0].y);
      for (let i = 1; i < stroke.length; i++) {
        ctx.lineTo(stroke[i].x, stroke[i].y);
      }
      ctx.stroke();
    }
  };

  const handlePointerDown = (e: React.PointerEvent) => {
    if (readOnly) return;
    e.preventDefault();
    const canvas = canvasRef.current;
    if (canvas) canvas.setPointerCapture(e.pointerId);
    setDrawing(true);
    currentStrokeRef.current = [getPos(e)];
  };

  const handlePointerMove = (e: React.PointerEvent) => {
    if (!drawing || readOnly) return;
    e.preventDefault();
    const pos = getPos(e);
    currentStrokeRef.current.push(pos);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    const pts = currentStrokeRef.current;
    if (pts.length < 2) return;
    ctx.strokeStyle = '#111';
    ctx.lineWidth = 2;
    ctx.lineCap = 'round';
    ctx.beginPath();
    ctx.moveTo(pts[pts.length - 2].x, pts[pts.length - 2].y);
    ctx.lineTo(pos.x, pos.y);
    ctx.stroke();
  };

  const handlePointerUp = (e: React.PointerEvent) => {
    if (!drawing) return;
    e.preventDefault();
    setDrawing(false);
    if (currentStrokeRef.current.length > 1) {
      strokesRef.current.push([...currentStrokeRef.current]);
      setHasContent(true);
      const strokeData = JSON.stringify({ version: 1, strokes: strokesRef.current });
      const dataUrl = canvasRef.current?.toDataURL('image/png') ?? '';
      onSave(fieldKey, dataUrl, strokeData);
    }
    currentStrokeRef.current = [];
  };

  const handleClear = () => {
    strokesRef.current = [];
    currentStrokeRef.current = [];
    setHasContent(false);
    const canvas = canvasRef.current;
    if (canvas) {
      const ctx = canvas.getContext('2d');
      ctx?.clearRect(0, 0, canvas.width, canvas.height);
    }
    onSave(fieldKey, '', '{}');
  };

  return (
    <div>
      <canvas
        ref={canvasRef}
        width={500}
        height={150}
        onPointerDown={handlePointerDown}
        onPointerMove={handlePointerMove}
        onPointerUp={handlePointerUp}
        onPointerLeave={handlePointerUp}
        style={{
          width: '100%',
          maxWidth: 500,
          height: 150,
          border: readOnly ? 'none' : '1px solid var(--border)',
          borderRadius: 'var(--radius-md)',
          cursor: readOnly ? 'default' : 'crosshair',
          touchAction: 'none',
          background: readOnly ? 'transparent' : '#fff',
          display: 'block',
        }}
      />
      {!readOnly && hasContent && (
        <button
          onClick={handleClear}
          style={{
            marginTop: 6,
            background: 'none',
            border: 'none',
            color: 'var(--color-danger-text, #dc2626)',
            fontSize: 'var(--font-size-xs)',
            cursor: 'pointer',
            padding: 0,
          }}
        >
          Clear signature
        </button>
      )}
    </div>
  );
}

// ─── Signature Preview ───

export function SignaturePreview({
  strokeData,
  width,
  height,
}: {
  strokeData: string;
  width: number;
  height: number;
}) {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    try {
      const parsed = JSON.parse(strokeData);
      if (!parsed.strokes) return;
      const sx = canvas.width / 500;
      const sy = canvas.height / 150;
      ctx.strokeStyle = '#111';
      ctx.lineWidth = 1;
      ctx.lineCap = 'round';
      for (const stroke of parsed.strokes) {
        if (stroke.length < 2) continue;
        ctx.beginPath();
        ctx.moveTo(stroke[0].x * sx, stroke[0].y * sy);
        for (let i = 1; i < stroke.length; i++) {
          ctx.lineTo(stroke[i].x * sx, stroke[i].y * sy);
        }
        ctx.stroke();
      }
    } catch {
      /* ignore */
    }
  }, [strokeData, width, height]);

  return (
    <canvas
      ref={canvasRef}
      width={width}
      height={height}
      style={{ width: '100%', height: '100%', display: 'block' }}
    />
  );
}

// ─── Field Input ───

export function FieldInput({
  field,
  value,
  onChange,
  readOnly,
}: {
  field: FormFieldDef;
  value: string;
  onChange: (val: string) => void;
  readOnly: boolean;
}) {
  const baseStyle: CSSProperties = {
    width: '100%',
    height: 36,
    padding: '0 10px',
    borderRadius: 'var(--radius-md)',
    border: '1px solid var(--border)',
    background: readOnly ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
    fontSize: 'var(--font-size-sm)',
    color: readOnly ? 'var(--text-muted)' : 'var(--text)',
    outline: 'none',
    boxSizing: 'border-box' as const,
  };

  switch (field.type) {
    // PRD.322 — Rich Text.
    //
    // READ-ONLY: formatted definition wording, no editor chrome, not an answer.
    // EDITABLE: the stored instance value once one exists, otherwise the
    // definition default. `resolveEditableRichText` owns that rule, so a
    // patient's saved wording never starts tracking a later definition edit.
    case 'rich-text': {
      const cfg = (field.config as RichTextConfig | undefined) ?? {};
      if (!cfg.editableWhenFilling) {
        return (
          <RichTextView doc={parseRichText(cfg.content ?? '')} style={{ color: 'var(--text)' }} />
        );
      }
      const doc = resolveEditableRichText(value === '' ? '' : (value ?? null), cfg.content);
      if (readOnly) {
        return <RichTextView doc={doc} style={{ color: 'var(--text-muted)' }} />;
      }
      return (
        <RichTextEditor
          ariaLabel={field.label || 'Rich text'}
          value={stringifyRichText(doc)}
          onChange={onChange}
          minHeight={120}
        />
      );
    }
    case 'text':
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          style={baseStyle}
        />
      );
    case 'textarea':
      return (
        <textarea
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          rows={3}
          style={{
            ...baseStyle,
            height: 'auto',
            minHeight: 72,
            padding: '8px 10px',
            resize: 'vertical',
          }}
        />
      );
    case 'number':
      return (
        <input
          type="number"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          placeholder={field.helpText}
          style={baseStyle}
        />
      );
    case 'date':
      return (
        <input
          type="date"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      );
    case 'select':
      return (
        <select
          value={value}
          onChange={(e) => onChange(e.target.value)}
          disabled={readOnly}
          style={baseStyle}
        >
          <option value="">Select...</option>
          {(field.options ?? []).map((opt) => (
            <option key={opt.id} value={opt.value}>
              {opt.label}
            </option>
          ))}
        </select>
      );
    case 'multi-select': {
      const selected = value
        ? value.startsWith('[')
          ? (JSON.parse(value) as string[])
          : [value]
        : [];
      return (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {(field.options ?? []).map((opt) => {
            const checked = selected.includes(opt.value);
            return (
              <label
                key={opt.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  cursor: readOnly ? 'default' : 'pointer',
                  fontSize: 'var(--font-size-sm)',
                  color: 'var(--text)',
                }}
              >
                <input
                  type="checkbox"
                  checked={checked}
                  disabled={readOnly}
                  onChange={() => {
                    const next = checked
                      ? selected.filter((v) => v !== opt.value)
                      : [...selected, opt.value];
                    onChange(JSON.stringify(next));
                  }}
                />
                {opt.label}
              </label>
            );
          })}
        </div>
      );
    }
    case 'yes-no':
      return (
        <div style={{ display: 'flex', gap: 16 }}>
          {['yes', 'no'].map((opt) => (
            <label
              key={opt}
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                cursor: readOnly ? 'default' : 'pointer',
                fontSize: 'var(--font-size-sm)',
                color: 'var(--text)',
              }}
            >
              <input
                type="radio"
                name={field.key}
                value={opt}
                checked={value === opt}
                disabled={readOnly}
                onChange={(e) => onChange(e.target.value)}
                style={{ accentColor: 'var(--color-primary)' }}
              />
              {opt.charAt(0).toUpperCase() + opt.slice(1)}
            </label>
          ))}
        </div>
      );
    default:
      return (
        <input
          type="text"
          value={value}
          onChange={(e) => onChange(e.target.value)}
          readOnly={readOnly}
          style={baseStyle}
        />
      );
  }
}

// ─── Section Renderer ───

export function SectionRenderer({
  section,
  values,
  signatures,
  onValueChange,
  onSignatureSave,
  readOnly,
  bindingData,
}: {
  section: FormSection;
  values: Record<string, string>;
  signatures?: Record<string, string>;
  onValueChange: (fieldKey: string, val: string) => void;
  onSignatureSave?: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
  bindingData: Record<string, string>;
}) {
  return (
    <div
      style={{
        background: 'var(--bg-surface-2, #f9fafb)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-1)',
      }}
    >
      <h3
        style={{
          margin: 0,
          fontSize: 'var(--font-size-lg)',
          fontWeight: 'var(--font-weight-semi)',
          color: 'var(--text)',
        }}
      >
        {section.title || 'Section'}
      </h3>
      {section.description && (
        <p style={{ margin: 0, fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
          {section.description}
        </p>
      )}
      <div
        style={{
          display: 'grid',
          gridTemplateColumns: '1fr 1fr',
          gap: 'var(--sp-4)',
          marginTop: 'var(--sp-3)',
        }}
      >
        {section.fields.map((field) => {
          const isFullWidth =
            field.layout === 'full' ||
            field.type === 'textarea' ||
            field.type === 'signature' ||
            field.type === 'drawing';
          return (
            <div
              key={field.key}
              style={{
                gridColumn: isFullWidth ? '1 / -1' : undefined,
                display: 'flex',
                flexDirection: 'column',
                gap: 'var(--sp-2)',
              }}
            >
              <label
                style={{
                  fontSize: 'var(--font-size-sm)',
                  fontWeight: 'var(--font-weight-medium)',
                  color: 'var(--text)',
                }}
              >
                {field.label || field.key}
                {field.required && (
                  <span style={{ color: 'var(--color-danger-text, #dc2626)' }}> *</span>
                )}
              </label>

              {field.type === 'signature' && onSignatureSave ? (
                <SignaturePad
                  fieldKey={field.key}
                  existingData={signatures?.[field.key] ?? null}
                  onSave={onSignatureSave}
                  readOnly={readOnly}
                />
              ) : field.type === 'drawing' ? (
                <div
                  style={{
                    border: '2px dashed rgba(22,163,74,0.4)',
                    borderRadius: 'var(--radius-md)',
                    padding: 'var(--sp-4)',
                    textAlign: 'center',
                    color: '#16a34a',
                    fontSize: 'var(--font-size-sm)',
                    background: 'rgba(22,163,74,0.04)',
                    minHeight: 80,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  Drawing area (coming soon)
                </div>
              ) : (
                <FieldInput
                  field={field}
                  value={resolveBoundFieldValue(field, values, bindingData) ?? ''}
                  onChange={(val) => onValueChange(field.key, val)}
                  readOnly={readOnly || (!!field.binding && !isFieldEditable(field))}
                />
              )}

              {field.helpText &&
                field.type !== 'text' &&
                field.type !== 'textarea' &&
                field.type !== 'number' && (
                  <span
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-placeholder, #9ca3af)',
                    }}
                  >
                    {field.helpText}
                  </span>
                )}
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Mode Switch ───

export function ModeSwitch({
  mode,
  onChange,
  primary,
}: {
  mode: 'form' | 'render';
  onChange: (m: 'form' | 'render') => void;
  primary: string;
}) {
  return (
    <div style={{ display: 'flex', gap: 0 }}>
      {(['render', 'form'] as const).map((m) => {
        const active = mode === m;
        return (
          <button
            key={m}
            onClick={() => onChange(m)}
            style={{
              height: 30,
              padding: '0 12px',
              border: '1px solid var(--border)',
              background: active ? primary : 'var(--bg-surface)',
              color: active ? '#fff' : 'var(--text)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)',
              cursor: 'pointer',
              borderRadius:
                m === 'render'
                  ? 'var(--radius-md) 0 0 var(--radius-md)'
                  : '0 var(--radius-md) var(--radius-md) 0',
              marginLeft: m === 'form' ? -1 : 0,
            }}
          >
            {m === 'render' ? 'Render' : 'Form'}
          </button>
        );
      })}
    </div>
  );
}

// ─── Template Overlay View (Render mode) ───

export function TemplateOverlayView({
  pages,
  mappings,
  fields,
  values,
  signatures,
  schema,
  bindingData,
  onValueChange,
  onSignatureSave,
  readOnly,
}: {
  pages: FormTemplatePageRow[];
  mappings: FormTemplateMappingRow[];
  fields: FormFieldDef[];
  values: Record<string, string>;
  signatures: Record<string, string>;
  schema: FormFieldsSchemaV1;
  bindingData: Record<string, string>;
  onValueChange: (fieldKey: string, val: string) => void;
  onSignatureSave: (fieldKey: string, dataUrl: string, strokeData: string) => void;
  readOnly: boolean;
}) {
  const [selectedPageIdx, setSelectedPageIdx] = useState(0);
  const [imageError, setImageError] = useState(false);
  const [imageLoading, setImageLoading] = useState(true);
  const [editingField, setEditingField] = useState<{ key: string; mappingId: string } | null>(null);
  const [editValue, setEditValue] = useState('');
  const [sigModalField, setSigModalField] = useState<string | null>(null);
  const currentPage = pages[selectedPageIdx];
  const pageMappings = useMemo(
    () => (currentPage ? mappings.filter((m) => m.page_id === currentPage.id) : []),
    [mappings, currentPage],
  );

  useEffect(() => {
    setImageError(false);
    setImageLoading(true);
  }, [selectedPageIdx]);

  // If no pages or images failed to load → show values summary
  if (pages.length === 0 || imageError) {
    return (
      <div style={{ padding: 'var(--sp-5)' }}>
        {pages.length > 0 && imageError && (
          <div
            style={{
              padding: 'var(--sp-3)',
              marginBottom: 'var(--sp-4)',
              borderRadius: 'var(--radius-md)',
              background: 'var(--color-warning-bg, #fefce8)',
              border: '1px solid var(--color-warning-text, #a16207)',
              color: 'var(--color-warning-text, #a16207)',
              fontSize: 'var(--font-size-sm)',
            }}
          >
            Template images are unavailable. Showing values summary instead.
          </div>
        )}
        <div
          style={{ maxWidth: 720, display: 'flex', flexDirection: 'column', gap: 'var(--sp-4)' }}
        >
          {schema.sections.map((section) => (
            <div
              key={section.id}
              style={{
                background: 'var(--bg-surface-2, #f9fafb)',
                border: '1px solid var(--border)',
                borderRadius: 'var(--radius-lg)',
                padding: 'var(--sp-5)',
              }}
            >
              <h3
                style={{
                  margin: '0 0 var(--sp-3)',
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                  color: 'var(--text)',
                }}
              >
                {section.title || 'Section'}
              </h3>
              <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--sp-3)' }}>
                {section.fields.map((field) => {
                  const val = resolveInstanceValue(field, values, signatures, bindingData);
                  const isSig = field.type === 'signature';
                  return (
                    <div
                      key={field.key}
                      style={{ display: 'flex', gap: 'var(--sp-3)', alignItems: 'flex-start' }}
                    >
                      <span
                        style={{
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text-muted)',
                          minWidth: 140,
                          flexShrink: 0,
                        }}
                      >
                        {field.label || field.key}
                      </span>
                      {isSig && signatures[field.key] ? (
                        <SignaturePreview
                          strokeData={signatures[field.key]}
                          width={200}
                          height={60}
                        />
                      ) : (
                        <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text)' }}>
                          {val}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const hasPrev = selectedPageIdx > 0;
  const hasNext = selectedPageIdx < pages.length - 1;

  const pageNavBtnStyle = (disabled: boolean): CSSProperties => ({
    height: 32,
    padding: '0 14px',
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)' as const,
    border: '1px solid var(--border)',
    borderRadius: 'var(--radius-sm)',
    background: disabled ? 'var(--bg-surface-2)' : 'var(--bg-surface)',
    color: disabled ? 'var(--text-placeholder, #9ca3af)' : 'var(--text)',
    cursor: disabled ? 'default' : 'pointer',
    display: 'flex',
    alignItems: 'center',
    gap: 4,
    opacity: disabled ? 0.5 : 1,
  });

  const PageNav = () => (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 'var(--sp-3)',
      }}
    >
      <button
        disabled={!hasPrev}
        onClick={() => hasPrev && setSelectedPageIdx(selectedPageIdx - 1)}
        style={pageNavBtnStyle(!hasPrev)}
      >
        <NavIcon name="chevron-left" /> Previous
      </button>
      <div style={{ display: 'flex', gap: 4 }}>
        {pages.map((p, i) => (
          <button
            key={p.id}
            onClick={() => setSelectedPageIdx(i)}
            style={{
              width: 28,
              height: 28,
              borderRadius: 'var(--radius-sm)',
              border: '1px solid var(--border)',
              background: i === selectedPageIdx ? 'var(--color-primary)' : 'var(--bg-surface)',
              color: i === selectedPageIdx ? '#fff' : 'var(--text)',
              fontSize: 'var(--font-size-xs)',
              fontWeight: 'var(--font-weight-medium)' as const,
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            {p.page_number}
          </button>
        ))}
      </div>
      <button
        disabled={!hasNext}
        onClick={() => hasNext && setSelectedPageIdx(selectedPageIdx + 1)}
        style={pageNavBtnStyle(!hasNext)}
      >
        Next <NavIcon name="chevron-right" />
      </button>
    </div>
  );

  return (
    <div
      style={{
        padding: 'var(--sp-5)',
        display: 'flex',
        flexDirection: 'column',
        gap: 'var(--sp-4)',
      }}
    >
      {pages.length > 1 && <PageNav />}

      {currentPage && (
        <div style={{ position: 'relative', display: 'inline-block', alignSelf: 'center' }}>
          <style>{`@keyframes _form_spin { to { transform: rotate(360deg) } }`}</style>
          <img
            src={currentPage.image_url}
            alt={`Page ${currentPage.page_number}`}
            style={{
              maxWidth: '100%',
              display: 'block',
              borderRadius: 'var(--radius-md)',
              border: '1px solid var(--border)',
            }}
            onLoad={() => setImageLoading(false)}
            onError={() => {
              setImageLoading(false);
              setImageError(true);
            }}
          />
          {imageLoading && (
            <div
              style={{
                position: 'absolute',
                inset: 0,
                background: 'rgba(240,240,240,0.7)',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: 8,
                borderRadius: 'var(--radius-md)',
                color: 'var(--text-muted)',
                fontSize: 'var(--font-size-sm)',
                zIndex: 10,
              }}
            >
              <svg
                width="20"
                height="20"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                style={{ animation: '_form_spin 1s linear infinite' }}
              >
                <path d="M21 12a9 9 0 1 1-6.219-8.56" />
              </svg>
              Loading...
            </div>
          )}
          {pageMappings.map((m) => {
            const field = fields.find((f) => f.key === m.field_key);
            if (!field) return null;
            const isSignature = field.type === 'signature';
            const isDrawing = field.type === 'drawing';
            const isBoundReadOnly = !!field.binding && !isFieldEditable(field);
            const isEditable = !readOnly && !isDrawing && !isBoundReadOnly;
            const val = resolveInstanceValue(field, values, signatures, bindingData);
            const isEditingThis =
              editingField?.key === field.key && editingField?.mappingId === m.id;
            // Parse mapping config for text alignment
            const mConfig = (() => {
              try {
                return typeof m.config === 'string'
                  ? JSON.parse(m.config || '{}')
                  : (m.config ?? {});
              } catch {
                return {};
              }
            })() as { textAlign?: string };
            const justifyMap: Record<string, string> = {
              left: 'flex-start',
              center: 'center',
              right: 'flex-end',
            };
            const justify = justifyMap[mConfig.textAlign ?? 'center'] ?? 'center';

            return (
              <div
                key={m.id}
                style={{
                  position: 'absolute',
                  left: `${m.x_pct}%`,
                  top: `${m.y_pct}%`,
                  width: `${m.w_pct}%`,
                  height: `${m.h_pct}%`,
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: justify,
                  fontSize: 11,
                  color: isDrawing ? '#16a34a' : isSignature ? '#dc2626' : '#111',
                  fontWeight: 500,
                  overflow: isEditingThis ? 'visible' : 'hidden',
                  padding: 2,
                  background: readOnly
                    ? 'transparent'
                    : isDrawing
                      ? 'rgba(22,163,74,0.08)'
                      : isSignature
                        ? 'rgba(220,38,38,0.06)'
                        : 'transparent',
                  border: readOnly
                    ? 'none'
                    : isDrawing
                      ? '2px dashed rgba(22,163,74,0.4)'
                      : isSignature
                        ? '2px dashed rgba(220,38,38,0.4)'
                        : isEditingThis
                          ? '2px solid var(--color-primary, #2563eb)'
                          : isEditable
                            ? '1px dashed rgba(37,99,235,0.35)'
                            : undefined,
                  borderRadius: isDrawing || isSignature || isEditable ? 4 : 1,
                  cursor: isEditable ? 'pointer' : 'default',
                  zIndex: isEditingThis ? 20 : undefined,
                }}
                onClick={() => {
                  if (!isEditable) return;
                  if (isSignature) {
                    setSigModalField(field.key);
                  } else {
                    setEditingField({ key: field.key, mappingId: m.id });
                    setEditValue(resolveBoundFieldValue(field, values, bindingData) ?? '');
                  }
                }}
              >
                {isSignature && signatures[field.key] ? (
                  <SignaturePreview strokeData={signatures[field.key]} width={100} height={40} />
                ) : (
                  val
                )}

                {isEditable && !isEditingThis && (
                  <span
                    style={{
                      position: 'absolute',
                      right: -1,
                      top: -1,
                      width: 14,
                      height: 14,
                      borderRadius: '0 4px 0 4px',
                      background: 'rgba(37,99,235,0.35)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <svg
                      width="8"
                      height="8"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="#fff"
                      strokeWidth="3"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    >
                      <path d="M17 3a2.85 2.85 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z" />
                    </svg>
                  </span>
                )}

                {isEditingThis && !isSignature && (
                  <div
                    onClick={(e) => e.stopPropagation()}
                    style={{
                      position: 'absolute',
                      top: '100%',
                      left: 0,
                      marginTop: 4,
                      background: '#fff',
                      border: '1px solid var(--border)',
                      borderRadius: 'var(--radius-md)',
                      boxShadow: '0 4px 12px rgba(0,0,0,0.15)',
                      padding: 'var(--sp-3)',
                      zIndex: 30,
                      minWidth: 220,
                      display: 'flex',
                      flexDirection: 'column',
                      gap: 'var(--sp-2)',
                    }}
                  >
                    <label style={{ fontSize: 11, fontWeight: 600, color: 'var(--text-muted)' }}>
                      {field.label || field.key}
                    </label>
                    {field.type === 'textarea' ? (
                      <textarea
                        autoFocus
                        rows={3}
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                          width: '100%',
                          fontSize: 13,
                          padding: '6px 8px',
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          resize: 'vertical',
                          fontFamily: 'inherit',
                        }}
                      />
                    ) : field.type === 'select' || field.type === 'multi-select' ? (
                      <select
                        autoFocus
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                          width: '100%',
                          fontSize: 13,
                          padding: '6px 8px',
                          height: 32,
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                        }}
                      >
                        <option value="">— Select —</option>
                        {(field.options ?? []).map((opt) => (
                          <option key={opt.id} value={opt.value}>
                            {opt.label}
                          </option>
                        ))}
                      </select>
                    ) : field.type === 'yes-no' ? (
                      <div style={{ display: 'flex', gap: 8 }}>
                        {['yes', 'no'].map((opt) => (
                          <label
                            key={opt}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 4,
                              fontSize: 13,
                              cursor: 'pointer',
                            }}
                          >
                            <input
                              type="radio"
                              name={`pin_${field.key}`}
                              value={opt}
                              checked={editValue === opt}
                              onChange={() => setEditValue(opt)}
                              style={{ accentColor: 'var(--color-primary)' }}
                            />
                            {opt.charAt(0).toUpperCase() + opt.slice(1)}
                          </label>
                        ))}
                      </div>
                    ) : (
                      <input
                        autoFocus
                        type={
                          field.type === 'number'
                            ? 'number'
                            : field.type === 'date'
                              ? 'date'
                              : 'text'
                        }
                        value={editValue}
                        onChange={(e) => setEditValue(e.target.value)}
                        style={{
                          width: '100%',
                          fontSize: 13,
                          padding: '6px 8px',
                          height: 32,
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          boxSizing: 'border-box',
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            onValueChange(field.key, editValue);
                            setEditingField(null);
                          } else if (e.key === 'Escape') {
                            setEditingField(null);
                          }
                        }}
                      />
                    )}
                    <div
                      style={{ display: 'flex', gap: 'var(--sp-2)', justifyContent: 'flex-end' }}
                    >
                      <button
                        onClick={() => {
                          onValueChange(field.key, '');
                          setEditValue('');
                          setEditingField(null);
                        }}
                        style={{
                          height: 28,
                          padding: '0 10px',
                          fontSize: 12,
                          border: '1px solid var(--border)',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--bg-surface)',
                          cursor: 'pointer',
                          color: 'var(--text-muted)',
                        }}
                      >
                        Clear
                      </button>
                      <button
                        onClick={() => {
                          onValueChange(field.key, editValue);
                          setEditingField(null);
                        }}
                        style={{
                          height: 28,
                          padding: '0 10px',
                          fontSize: 12,
                          border: 'none',
                          borderRadius: 'var(--radius-sm)',
                          background: 'var(--color-primary, #2563eb)',
                          color: '#fff',
                          cursor: 'pointer',
                          fontWeight: 600,
                        }}
                      >
                        Save
                      </button>
                    </div>
                  </div>
                )}
              </div>
            );
          })}

          {editingField && (
            <div
              style={{ position: 'fixed', inset: 0, zIndex: 15 }}
              onClick={() => setEditingField(null)}
            />
          )}
        </div>
      )}

      {pages.length > 1 && <PageNav />}

      {sigModalField && (
        <div
          style={{
            position: 'fixed',
            inset: 0,
            zIndex: 100,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          onClick={() => setSigModalField(null)}
        >
          <div style={{ position: 'absolute', inset: 0, background: 'rgba(0,0,0,0.4)' }} />
          <div
            onClick={(e) => e.stopPropagation()}
            style={{
              position: 'relative',
              background: '#fff',
              borderRadius: 'var(--radius-lg)',
              padding: 'var(--sp-5)',
              width: 540,
              maxWidth: '90vw',
              boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
              display: 'flex',
              flexDirection: 'column',
              gap: 'var(--sp-3)',
            }}
          >
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
              <h3
                style={{
                  margin: 0,
                  fontSize: 'var(--font-size-lg)',
                  fontWeight: 'var(--font-weight-semi)',
                }}
              >
                {fields.find((f) => f.key === sigModalField)?.label || 'Signature'}
              </h3>
              <button
                onClick={() => setSigModalField(null)}
                style={{
                  width: 28,
                  height: 28,
                  border: 'none',
                  background: 'none',
                  cursor: 'pointer',
                  fontSize: 18,
                  color: 'var(--text-muted)',
                }}
              >
                ×
              </button>
            </div>
            <SignaturePad
              fieldKey={sigModalField}
              existingData={signatures[sigModalField] ?? null}
              onSave={(fk, du, sd) => {
                onSignatureSave(fk, du, sd);
                setSigModalField(null);
              }}
              readOnly={false}
            />
          </div>
        </div>
      )}
    </div>
  );
}
